import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BottomSheet from "../ui/BottomSheet";
import Dialog from "../ui/Dialog";
import Button from "../ui/Button";
import { TextField } from "../ui/Field";
import { MIN_ADDRESS_QUERY_LENGTH, searchAddresses } from "../../services/addressService";

const SEARCH_DELAY_MS = 250;

// Met en gras la partie déjà tapée dans la suggestion (maquette 1m).
const HighlightedMatch = ({ text, query }) => {
  const index = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (!query.trim() || index < 0) return <span>{text}</span>;
  const end = index + query.trim().length;
  return (
    <span>
      {text.slice(0, index)}
      <b>{text.slice(index, end)}</b>
      {text.slice(end)}
    </span>
  );
};

// Écran 1m : coordonnées de la facture nominative. Ouvert après la
// préautorisation (1c), où il faut choisir entre « Enregistrer » et « Je ne
// souhaite pas de facture », et depuis la recharge en cours (1d), où il se ferme.
export default function InvoiceSheet({ open, email, initialValues, onSave, onSkip, onClose, saving = false, error }) {
  const { t } = useTranslation();
  const [lastName, setLastName] = useState(initialValues?.lastName || "");
  const [firstName, setFirstName] = useState(initialValues?.firstName || "");
  const [addressQuery, setAddressQuery] = useState(initialValues?.label || "");
  const [address, setAddress] = useState(initialValues?.street ? initialValues : null);
  const [suggestions, setSuggestions] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);

  // Recherche d'adresse : on attend une pause dans la frappe, et la recherche
  // précédente est annulée pour ne pas afficher des résultats périmés.
  useEffect(() => {
    if (address || addressQuery.trim().length < MIN_ADDRESS_QUERY_LENGTH) {
      setSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchAddresses(addressQuery.trim(), controller.signal)
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [addressQuery, address]);

  const errors = {
    lastName: !lastName.trim() ? t("invoice.fieldRequired") : "",
    firstName: !firstName.trim() ? t("invoice.fieldRequired") : "",
    address: !address ? (addressQuery.trim() ? t("invoice.addressPick") : t("invoice.fieldRequired")) : "",
  };
  const isValid = !errors.lastName && !errors.firstName && !errors.address;

  const handleSave = () => {
    setShowErrors(true);
    if (!isValid) return;
    onSave({
      lastName: lastName.trim(),
      firstName: firstName.trim(),
      street: address.street,
      postalCode: address.postalCode,
      city: address.city,
      label: address.label,
      country: "FRA",
    });
  };

  return (
    <>
      <BottomSheet open={open && !confirmSkip} onClose={onClose} title={t("invoice.title")} showClose>
        <p className="mb-5 text-sm leading-5 text-qv-muted">
          {email ? t("invoice.intro", { email }) : t("invoice.introNoEmail")}
        </p>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="invoice-last-name"
              label={`${t("invoice.lastName")} *`}
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              error={showErrors ? errors.lastName : ""}
            />
            <TextField
              id="invoice-first-name"
              label={`${t("invoice.firstName")} *`}
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              error={showErrors ? errors.firstName : ""}
            />
          </div>

          <div className="relative">
            <TextField
              id="invoice-address"
              label={`${t("invoice.address")} *`}
              placeholder={t("invoice.addressPlaceholder")}
              autoComplete="off"
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-controls="invoice-address-suggestions"
              value={addressQuery}
              onChange={(event) => {
                setAddressQuery(event.target.value);
                setAddress(null);
              }}
              error={showErrors ? errors.address : ""}
            />
            {suggestions.length > 0 && (
              <ul
                id="invoice-address-suggestions"
                role="listbox"
                className="absolute z-10 left-0 right-0 mt-1 flex flex-col overflow-hidden rounded-xl border border-qv-line bg-white p-1.5 shadow-qv-card"
              >
                {suggestions.map((suggestion) => (
                  <li key={suggestion.id} role="option" aria-selected="false">
                    <button
                      type="button"
                      onClick={() => {
                        setAddress(suggestion);
                        setAddressQuery(suggestion.label);
                        setSuggestions([]);
                      }}
                      className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium hover:bg-qv-brand-50"
                    >
                      <img src="/Map_Point.svg" alt="" className="h-[18px] w-[18px] shrink-0" />
                      <HighlightedMatch text={suggestion.label} query={addressQuery} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <p className="mt-2 text-xs leading-[18px] font-medium text-qv-muted">{t("invoice.required")}</p>
        {error && (
          <p className="mt-3 text-sm font-medium text-qv-red" role="alert">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-2.5">
          <Button onClick={handleSave} loading={saving}>
            {t("invoice.save")}
          </Button>
          {onSkip && (
            <Button variant="neutral" onClick={() => setConfirmSkip(true)}>
              {t("invoice.noInvoice")}
            </Button>
          )}
        </div>
      </BottomSheet>

      <Dialog
        open={open && confirmSkip}
        onClose={() => setConfirmSkip(false)}
        icon="document-text-linear"
        title={t("invoice.noInvoiceTitle")}
        text={t("invoice.noInvoiceText")}
      >
        <Button
          className="!h-[52px] !text-[15px]"
          onClick={() => {
            setConfirmSkip(false);
            onSkip();
          }}
        >
          {t("invoice.noInvoiceConfirm")}
        </Button>
        <Button variant="neutral" onClick={() => setConfirmSkip(false)}>
          {t("invoice.cancel")}
        </Button>
      </Dialog>
    </>
  );
}
